import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ContentApi } from "../../../shared/api/contentApi";
import { sortItems } from "../../../shared/helpers/SortByName";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import NewProjectDialog from "../components/NewProjectDialog";
import { notify } from "shared/utils/notify";
import { resolvePagedTotals } from "../../../shared/helpers/pagination";
import formatDate from '../../../shared/helpers/formatDate';
import UuidCell from '../../../components/ui/UuidCell';
import TableCellPreview from '../../../components/ui/TableCellPreview';

import { BaseTable } from "../../../components/ui/ant/Table";
import { Modal, Flex } from "antd";

export default function ProjectsPage() {
    const navigate = useNavigate();
    const [createOpen, setCreateOpen] = useState(false);
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    // серверная пагинация
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // когда меняется фильтр/поиск — сбрасываем страницу на 1
    useEffect(() => {
        setPage(1);
    }, [q, pageSize]);

    useEffect(() => {
        const ac = new AbortController();
        setLoading(true);
        setError("");

        ContentApi.getProjects(
            {
                search: q || undefined,
                page,
                pageSize,
            },
            { signal: ac.signal }
        )
            .then((data) => {
                setProjects(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e.message || "Ошибка загрузки");
            })
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [q, page, pageSize]);

    // сортируем ТОЛЬКО текущую страницу (если серверной сортировки нет)
    const pageItems = useMemo(
        () => sortItems(projects, sortBy, sortDir),
        [projects, sortBy, sortDir]
    );
    const safePage = Math.min(page, totalPages);

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        Проекты
                    </h1>
                    <p className="text-sm text-zinc-200">
                        Данные с SbContentAPI
                    </p>
                </div>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => setCreateOpen(true)}
                >
                    Создать проект
                </button>
                <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex flex-col">
                        <label className="eyebrow mb-1">
                            Поиск
                        </label>
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Имя или systemName…"
                            className="input w-64"
                            name="project-search"
                        />
                    </div>
                </div>
            </header>

            <NewProjectDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(created) => {
                    const newId = created?.id || created?.projectId;
                    if (newId) {
                        navigate(`/projects/${newId}`);
                    } else {
                        setPage(1);
                    }
                }}
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <BaseTable
                        columns={[
                            { key: "id", header: "ID", render: (value) => <UuidCell value={value} /> },
                            { key: "name", header: "Имя", sortable: true },
                            { key: "description", header: "Описание", render: (value) => <TableCellPreview value={value} maxLength={32} /> },
                            { key: "systemName", header: "Системное имя", sortable: true },
                            { 
                                key: "updatedAt", 
                                header: "Обновлено",
                                sortable: true,
                                render: (value) => <span title={value || '-'}>{formatDate(value)}</span> },
                        ]}
                        data={pageItems}
                        // Используем хук navigate, который у вас уже объявлен выше в компоненте
                        onRowClick={(row) => navigate(`/projects/${row.id}`)}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={(key, dir) => {
                            setSortBy(key);
                            if (dir) setSortDir(dir);
                        }}
                        pagination={{
                            current: safePage,
                            pageSize,
                            total,
                        }}
                        onChange={(pagination) => {
                            if (pagination?.current) {
                                setPage(pagination.current);
                            }
                            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
                                setPageSize(pagination.pageSize);
                            }
                        }}
                    />
                </>
            )}
        </section>
    );
}